import React, { createContext, useState, useContext, useEffect } from "react";
import { AuthContext } from "./AuthContext.js";
import { Link, Typography } from "@mui/material";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import { getPageRoute } from "src/services/Config.js";
import { customEvent } from "src/utils/gtag.js";
import marketingBanner from "../marketing-banner.json";
import {
  Features,
  hasAccess,
  isGoogleCalendarConnectionCandidate,
  getUpgradeFlow,
} from "src/services/FeatureGatingService.js";

const TopNavbarContext = createContext();

export const useTopNavbar = () => {
  return useContext(TopNavbarContext);
};

const MarketingBanner = ({ banner }) => {
  const subcopyParts = banner.subcopy.split(/({{.*?}})/g);
  const { user } = useContext(AuthContext);

  useEffect(() => {
    customEvent({
      name: `marketing_banner_${banner.tag}_load`,
      category: "MarketingBanner",
      label: "Banner Loaded",
      data: {
        user_id: user?._id,
        user_email: user?.email,
      },
    });
  }, []); // Empty dependency array to run only once on mount

  return (
    <Typography component="span">
      <b>{banner.title}</b>{" "}
      {subcopyParts.map((part, index) => {
        if (part.startsWith("{{")) {
          const key = part.slice(2, -2).trim();
          const link = banner.links[key];
          return (
            <Link
              key={index}
              component={RouterLink}
              to={getPageRoute(...link.route)}
              color="inherit"
              onClick={() => {
                customEvent({
                  name: `marketing_banner_${banner.tag}_click`,
                  category: "MarketingBanner",
                  label: key,
                  data: {
                    user_id: user?._id,
                    user_email: user?.email,
                    link_text: link.text,
                    link_route: link.route.join("/"),
                  },
                });
              }}
            >
              {link.text}
            </Link>
          );
        }
        return <span key={index}>{part}</span>;
      })}
    </Typography>
  );
};

export const TopNavbarProvider = ({ children }) => {
  const [showTopNavbar, setShowTopNavbar] = useState(false);
  const [topNavbarContent, setTopNavbarContent] = useState(null);
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const getUserStatus = () => {
      if (
        (!user || hasAccess(user, Features.GOOGLE_CALENDAR_SYNC.key)) &&
        ([
          getPageRoute("home", "HOME"),
          getPageRoute("chat", "SEARCH"),
        ].includes(location.pathname) ||
          location.pathname.startsWith(
            getPageRoute("home", "TAG").replace(":tag", "")
          ) ||
          location.pathname.includes(
            encodeURIComponent(marketingBanner[0]?.tag)
          ))
      ) {
        return "MARKETING_BANNER";
      }
      if (user?.surpassedSubscriptionBenefits) {
        return "SURPASSED_SUBSCRIPTION_BENEFITS";
      }
      if (user?.needsConsumerCalendarUpgrade) {
        return "UPGRADE_REQUIRED";
      }
      if (user?.hasInvalidRefreshToken) {
        return "INVALID_REFRESH_TOKEN";
      }
      if (isGoogleCalendarConnectionCandidate(user)) {
        return "GOOGLE_CONNECT";
      }
      if (user && !hasAccess(user, Features.CONNECT_OTHER_CALENDAR.key)) {
        return "CONNECT";
      }
      return "NO_NAVBAR";
    };

    switch (getUserStatus()) {
      case "MARKETING_BANNER":
        const currentBanner = marketingBanner[0];
        setTopNavbarContent(<MarketingBanner banner={currentBanner} />);
        setShowTopNavbar(true);
        break;
      case "SURPASSED_SUBSCRIPTION_BENEFITS":
        setTopNavbarContent(
          <Typography component="span">
            {" "}
            ‼️ <b>Important:</b> &nbsp;
            <span>
              Your trial or Stanza+ membership has expired. Please either
              upgrade to
            </span>{" "}
            <Link
              color="inherit"
              onClick={() =>
                navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
                  state: { source: "top_banner_expired" },
                })
              }
            >
              Stanza+
            </Link>{" "}
            <span>or downgrade to a</span>{" "}
            <Link
              component={RouterLink}
              to={getPageRoute("help", "CHOOSE_FREE_PLAN")}
              color="inherit"
            >
              free plan.
            </Link>
          </Typography>
        );
        setShowTopNavbar(true);
        break;
      case "UPGRADE_REQUIRED":
        setTopNavbarContent(
          <Typography component="span">
            {" "}
            <span>
              You've hit the limit of free calendar subscriptions. Upgrade
              to&nbsp;
            </span>
            <Link
              color="inherit"
              onClick={() => {
                const upgradeFlow = getUpgradeFlow(
                  Features.STANDARD_UPGRADE.key
                );
                navigate(upgradeFlow.route, {
                  state: { ...upgradeFlow.state, source: "top_banner_limit" },
                });
              }}
            >
              Stanza+
            </Link>
            <span>&nbsp;to go unlimited.</span>
          </Typography>
        );
        setShowTopNavbar(true);
        break;

      case "GOOGLE_CONNECT":
        setTopNavbarContent(
          <Typography component="span">
            {" "}
            🎉 <b>New:</b> &nbsp;
            <Link
              component={RouterLink}
              onClick={() => {
                customEvent({
                  name: "nav_google_calendar_steps_top_banner",
                  category: "GoogleCalendarExperience",
                  label:
                    "Navigate to Google Calendar Steps from Top Banner Click",
                  data: {
                    user_id: user._id,
                    user_email: user.email,
                    user_handle: user.handle ?? "no_handle",
                  },
                });
              }}
              to={getPageRoute(
                "help",
                "SUBSCRIBING_GOOGLE_CALENDAR_STEPS_STANDALONE"
              )}
              color="inherit"
            >
              Connect your Google Calendar
            </Link>
            <span>&nbsp;for faster updates, across all devices. &nbsp;</span>
            <Link
              component={RouterLink}
              onClick={() =>
                window.open(
                  "https://stanza-1.gitbook.io/stanza-help-center/for-subscribers/stanzaplus/google-connect",
                  "_blank"
                )
              }
              color="inherit"
            >
              Watch a demo
            </Link>
            <span>&nbsp;of how it works.</span>
          </Typography>
        );
        setShowTopNavbar(true);
        break;

      case "CONNECT":
        setTopNavbarContent(
          <Typography component="span">
            {" "}
            🎉 <b>New:</b> &nbsp;
            <Link
              component={RouterLink}
              onClick={() => {
                customEvent({
                  name: "nav_other_calendar_steps_top_banner",
                  category: "OtherCalendarExperience",
                  label:
                    "Navigate to Other Calendar Steps from Top Banner Click",
                  data: {
                    user_id: user._id,
                    user_email: user.email,
                    user_handle: user.handle ?? "no_handle",
                  },
                });
              }}
              to={getPageRoute("help", "SUBSCRIBING_CALENDAR_STEPS_STANDALONE")}
              color="inherit"
            >
              Transparent updates
            </Link>
            <span>—know when your calendar app last refreshed. &nbsp;</span>
            <Link
              component={RouterLink}
              onClick={() =>
                window.open(
                  "https://stanza-1.gitbook.io/stanza-help-center/for-subscribers/stanzaplus/transparent-updates",
                  "_blank"
                )
              }
              color="inherit"
            >
              How it works.
            </Link>
          </Typography>
        );
        setShowTopNavbar(true);
        break;

      case "INVALID_REFRESH_TOKEN":
        setTopNavbarContent(
          <Typography component="span">
            {" "}
            ‼️ <b>Important:</b> &nbsp;
            <span>There is an issue with your Google Calendar connection.</span>
            &nbsp;
            <Link
              component={RouterLink}
              to={getPageRoute("help", "CONNECT_GOOGLE_CALENDAR")}
              color="inherit"
            >
              Reconnect now.
            </Link>
          </Typography>
        );
        setShowTopNavbar(true);
        break;

      case "NO_NAVBAR":
      default:
        setShowTopNavbar(false);
        break;
    }
  }, [user, location.pathname]);

  return (
    <TopNavbarContext.Provider
      value={{
        showTopNavbar,
        setShowTopNavbar,
        topNavbarContent,
        setTopNavbarContent,
      }}
    >
      {children}
    </TopNavbarContext.Provider>
  );
};
